import axios from "axios"
import { Location } from "../../utils/locations"
import { Dispatch, RNBThunkAction } from "../store.config"

const enum LocationsActionsEnum {
  ADD_LOCATIONS_ATTEMPT = "LOCATIONS/addLocationsAttempt",
  ADD_LOCATIONS_SUCCESS = "LOCATIONS/addLocationsSuccess",
  ADD_LOCATIONS_ERROR = "LOCATIONS/addLocationsError",
  UPDATE_LOCATIONS_ATTEMPT = "LOCATIONS/updateLocationsAttempt",
  UPDATE_LOCATIONS_SUCCESS = "LOCATIONS/updateLocationsSuccess",
  UPDATE_LOCATIONS_ERROR = "LOCATIONS/updateLocationsError",
  DELETE_LOCATION_ATTEMPT = "LOCATIONS/deleteLocationAttempt",
  DELETE_LOCATION_SUCCESS = "LOCATIONS/deleteLocationSuccess",
  DELETE_LOCATION_ERROR = "LOCATIONS/deleteLocationError",
  GET_LOCATIONS_ATTEMPT = "LOCATIONS/getLocationsAttempt",
  GET_LOCATIONS_SUCCESS = "LOCATIONS/getLocationsSuccess",
  GET_LOCATIONS_ERROR = "LOCATIONS/getLocationsError",
  EDIT_LOCATION = "LOCATIONS/editLocation",
}

export const addLocationsAttemptAction = () =>
  ({ type: LocationsActionsEnum.ADD_LOCATIONS_ATTEMPT } as const)

export const addLocationsSuccessAction = () =>
  ({
    type: LocationsActionsEnum.ADD_LOCATIONS_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "locations_axes.toaster.title",
      bodyKey: "locations_axes.toaster.body",
    },
  } as const)

export const addLocationsErrorAction = () =>
  ({
    type: LocationsActionsEnum.ADD_LOCATIONS_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "locations_axes.toaster.error.title",
      bodyKey: "locations_axes.toaster.error.body",
    },
  } as const)

export const addLocationsTooLongErrorAction = (errorData?: {
  current_length: number
  limit: number
}) =>
  ({
    type: LocationsActionsEnum.ADD_LOCATIONS_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "locations_axes.toaster.too_long.title",
      bodyKey: "locations_axes.toaster.too_long.body",
      bodyValues: errorData,
    },
  } as const)

export const updateLocationsAttemptAction = () =>
  ({ type: LocationsActionsEnum.UPDATE_LOCATIONS_ATTEMPT } as const)

export const updateLocationsSuccessAction = () =>
  ({
    type: LocationsActionsEnum.UPDATE_LOCATIONS_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "locations_axes.toaster.title",
      bodyKey: "locations_axes.toaster.body",
    },
  } as const)

export const updateLocationsErrorAction = () =>
  ({
    type: LocationsActionsEnum.UPDATE_LOCATIONS_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "locations_axes.toaster.error.title",
      bodyKey: "locations_axes.toaster.error.body",
    },
  } as const)

export const deleteLocationAttemptAction = () =>
  ({ type: LocationsActionsEnum.DELETE_LOCATION_ATTEMPT } as const)

export const deleteLocationSuccessAction = () =>
  ({ type: LocationsActionsEnum.DELETE_LOCATION_SUCCESS } as const)

export const deleteLocationErrorAction = () =>
  ({ type: LocationsActionsEnum.DELETE_LOCATION_ERROR } as const)

export const getLocationsAttemptAction = () =>
  ({ type: LocationsActionsEnum.GET_LOCATIONS_ATTEMPT } as const)

export const getLocationsSuccessAction = (locations: Location[]) =>
  ({
    type: LocationsActionsEnum.GET_LOCATIONS_SUCCESS,
    payload: locations,
  } as const)

export const getLocationsErrorAction = () =>
  ({ type: LocationsActionsEnum.GET_LOCATIONS_ERROR } as const)

export const editLocationAction = (location: Location) =>
  ({
    type: LocationsActionsEnum.EDIT_LOCATION,
    payload: location,
  } as const)

type LocationsActionsType = ReturnType<
  | typeof addLocationsAttemptAction
  | typeof addLocationsSuccessAction
  | typeof addLocationsErrorAction
  | typeof addLocationsTooLongErrorAction
  | typeof updateLocationsAttemptAction
  | typeof updateLocationsSuccessAction
  | typeof updateLocationsErrorAction
  | typeof deleteLocationAttemptAction
  | typeof deleteLocationSuccessAction
  | typeof deleteLocationErrorAction
  | typeof getLocationsAttemptAction
  | typeof getLocationsSuccessAction
  | typeof getLocationsErrorAction
  | typeof editLocationAction
>

export const locationsInitialState: LocationsState = {
  locations: [],
  editedLocations: [],
}

export interface LocationsState {
  locations: Location[]
  editedLocations: Location[]
}

export function locationsReducer(
  state = locationsInitialState,
  action: LocationsActionsType
): LocationsState {
  switch (action.type) {
    case LocationsActionsEnum.GET_LOCATIONS_SUCCESS:
      return { ...state, locations: action.payload, editedLocations: [] }
    case LocationsActionsEnum.EDIT_LOCATION:
      const filteredEditedLocations = state.editedLocations.filter(
        (location) => location.id !== action.payload.id
      )
      return {
        ...state,
        editedLocations: [...filteredEditedLocations, action.payload],
      }
    default:
      return { ...state }
  }
}

export const addLocationsThunk =
  ({ companyId, locations }: { companyId: number; locations: string[] }) =>
  (dispatch: Dispatch<LocationsActionsType | RNBThunkAction>) => {
    const cleanedLocations = locations.filter((a) => Boolean(a))
    dispatch(addLocationsAttemptAction())
    return axios
      .post(`locations/add_locations`, {
        company_id: companyId,
        locations: cleanedLocations,
      })
      .then(() => {
        dispatch(addLocationsSuccessAction())
        dispatch(getLocationsThunk({ companyId }))
      })
      .catch((error) => {
        const errorData = error.response?.data?.error
        if (
          typeof errorData === "object" &&
          errorData.type === "location_too_long"
        ) {
          dispatch(
            addLocationsTooLongErrorAction({
              current_length: errorData.current_length,
              limit: errorData.limit,
            })
          )
        } else {
          dispatch(addLocationsErrorAction())
        }
      })
  }

export const deleteLocationThunk =
  ({ companyId, location }: { companyId: number; location: Location }) =>
  (dispatch: Dispatch<LocationsActionsType | RNBThunkAction>) => {
    dispatch(deleteLocationAttemptAction())
    return axios
      .delete(`locations/delete_location`, { params: { id: location.id } })
      .then(() => {
        dispatch(deleteLocationSuccessAction())
        dispatch(getLocationsThunk({ companyId }))
      })
      .catch(() => {
        dispatch(deleteLocationErrorAction())
      })
  }

export const updateLocationsThunk =
  ({ companyId, locations }: { companyId: number; locations: Location[] }) =>
  (dispatch: Dispatch<LocationsActionsType | RNBThunkAction>) => {
    dispatch(updateLocationsAttemptAction())
    return axios
      .put(`locations/update_locations`, {
        locations: locations.map((location) => ({
          id: location.id,
          description: location.description,
        })),
      })
      .then(() => {
        dispatch(updateLocationsSuccessAction())
        dispatch(getLocationsThunk({ companyId }))
      })
      .catch(() => {
        dispatch(updateLocationsErrorAction())
      })
  }

export const getLocationsThunk =
  ({ companyId }: { companyId: number }) =>
  (dispatch: Dispatch<LocationsActionsType>) => {
    dispatch(getLocationsAttemptAction())
    return axios
      .get(`locations/get_locations`, { params: { company_id: companyId } })
      .then(({ data }) => {
        dispatch(getLocationsSuccessAction(data))
      })
      .catch(() => {
        dispatch(getLocationsErrorAction())
      })
  }
